import { supabase } from "./supabase";

/**
 * The household's own settings.
 *
 * Two things live here that are genuinely desk work rather than phone work:
 * the working week, and how big a property's fence is. Both are typed once and
 * then relied on for months, which is exactly the sort of thing nobody wants to
 * do standing in a kitchen.
 *
 * The working week matters more than it looks. Since public holidays landed it
 * is not merely a roster: BCEA s18(2) pays somebody for a public holiday that
 * falls on a day they **would ordinarily work**, and s20(8) hands back a leave
 * day for the same reason. With no pattern recorded, every rule that reads it
 * treats each day as one they would work — which overpays a holiday rather than
 * quietly taking it away, but is a guess either way. Setting it makes the
 * register and the payslip correct rather than merely safe.
 */

export interface PatternDay {
  /** 0 = Sunday, matching JavaScript's getDay so nothing needs translating. */
  weekday: number;
  startsAt: string;
  endsAt: string;
}

export const WEEKDAY_NAMES = [
  "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",
];

/**
 * A read that can fail, and says so.
 *
 * "No rows" and "the read failed" are different answers, and a screen that
 * renders both as an empty list tells an employer that nobody works any days
 * when in fact nothing is known. Every loader here returns which happened.
 */
export type Loaded<T> =
  | { ok: true; value: T }
  | { ok: false; message: string };

export async function workPattern(employeeId: string): Promise<Loaded<PatternDay[]>> {
  const { data, error } = await supabase
    .from("work_pattern").select("weekday, starts_at, ends_at")
    .eq("employee_id", employeeId).order("weekday");
  if (error) return { ok: false, message: error.message };
  return {
    ok: true,
    value: ((data ?? []) as Record<string, any>[]).map((p) => ({
      weekday: p.weekday,
      startsAt: String(p.starts_at).slice(0, 5),
      endsAt: String(p.ends_at).slice(0, 5),
    })),
  };
}

/**
 * Replaces somebody's working days.
 *
 * Delete-then-insert rather than a diff, matching the app. A pattern is seven
 * rows at most, and the alternative is reconciliation code that can leave a day
 * behind — and a day left behind means somebody is marked absent on a day they
 * were never asked to work, which is the failure the feature exists to avoid.
 */
export async function setWorkPattern(
  employeeId: string, days: PatternDay[],
): Promise<boolean> {
  const { error: delErr } = await supabase
    .from("work_pattern").delete().eq("employee_id", employeeId);
  if (delErr) return false;
  if (days.length === 0) return true;

  const { error } = await supabase.from("work_pattern").insert(
    days.map((d) => ({
      employee_id: employeeId,
      weekday: d.weekday,
      starts_at: d.startsAt,
      ends_at: d.endsAt,
    })),
  );
  return !error;
}

export async function setFenceRadius(siteId: string, radiusM: number): Promise<boolean> {
  const { data, error } = await supabase
    .from("sites").update({ radius_m: radiusM }).eq("id", siteId).select("id");
  // RLS filters rather than refusing, so an empty result means "not allowed".
  return !error && (data ?? []).length > 0;
}

export interface Manager {
  userId: string;
  role: string;
  canApprove: boolean;
  canManagePeople: boolean;
  canChangeTerms: boolean;
  canManageSites: boolean;
  canManageManagers: boolean;
}

/**
 * Who administers this household and what they may do.
 *
 * Read-only here. Changing somebody's rights is the one setting that can hand
 * another person control of what everybody is paid, and it stays behind the
 * app's own sheet — a right granted by accident from a settings page is not a
 * right anybody meant to grant.
 */
export async function managers(orgId: string): Promise<Loaded<Manager[]>> {
  const { data, error } = await supabase
    .from("memberships")
    .select("user_id, role, can_approve, can_manage_people, can_change_terms," +
            " can_manage_sites, can_manage_managers")
    .eq("org_id", orgId);
  if (error) return { ok: false, message: error.message };
  return {
    ok: true,
    value: ((data ?? []) as Record<string, any>[]).map((m) => ({
      userId: m.user_id,
      role: m.role,
      canApprove: m.can_approve,
      canManagePeople: m.can_manage_people,
      canChangeTerms: m.can_change_terms,
      canManageSites: m.can_manage_sites,
      canManageManagers: m.can_manage_managers,
    })),
  };
}
